import {
    ChangeDetectionStrategy,
    Component,
    type ElementRef,
    computed,
    inject,
    input,
    linkedSignal,
    output,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { ChangeContext, Options } from '@angular-slider/ngx-slider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import type { SelectedFileInterface } from '@sendpulse/file-manager';
import { DirectoryItemCategory, FmSourceType } from '@sendpulse/file-manager';

import type { BackgroundAtDevice, BackgroundFile } from '@sites/entity-core';
import { backgroundAtDevice } from '@sites/entity-core';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { DecimalPipe } from '@angular/common';
import { SpFileManagerComponent } from '@libs/components/sp-file-manager/src/sp-file-manager.component';
import { SpDefaultUploadButtonComponent } from '@libs/components/sp-file-manager/src/sp-upload-buttons/sp-default-upload-button.component';
import { SpFilesSelectComponent } from '@libs/components/sp-file-manager/src/sp-files-select/sp-files-select.component';
import { SpControlHandleComponent } from '@libs/ui/sp-control-handle/src/sp-control-handle.component';
import { SpSliderComponent } from '@libs/ui/sp-slider/src/sp-slider.component';
import { SpColorPickerComponent } from '@libs/components/sp-color-picker/src/sp-color-picker.component';

import { PreviewType, type ReplacedFile } from '@components/sp-file-manager/src/types';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';

import { backgroundPositionOptions, EPositionOptions } from '@common/constants/background-position-options.const';
import { backgroundRepeatOptions, ERepeatOptions } from '@common/constants/background-repeat-options.const';
import { backgroundSizeOptions, ESizeOptions } from '@common/constants/background-size-options.const';

import { EFilerOverlay, EScrollEffect } from '@common/enums';
import { extendWebFileDataPromise } from '@common/helpers';
import { NotifyFacade } from '@common/utils/notify/notify.facade';

import type { SelectedFileExtended } from '@common/types/file.type';

import { UserFacade } from '@web/store/user/user.facade';
import { environment } from '@web-env/environment';

import { ImageSizeLimitModalComponent } from '@web/shared/modals/image-size-limit-modal/image-size-limit-modal.component';

type BackgroundLayout = Pick<BackgroundAtDevice, 'size' | 'position' | 'repeat'>;

const DEFAULT_LAYOUT: BackgroundLayout = {
    size: ESizeOptions.auto,
    position: EPositionOptions.center,
    repeat: ERepeatOptions.noRepeat,
};

@Component({
    selector: 'web-background-image-settings',
    templateUrl: './web-background-image-settings.component.html',
    styleUrls: ['./web-background-image-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TooltipModule,
        SpSwitchComponent,
        SpFileManagerComponent,
        SpDefaultUploadButtonComponent,
        SpFilesSelectComponent,
        SpControlHandleComponent,
        SpSliderComponent,
        SpColorPickerComponent,
        DecimalPipe,
        TranslateModule,
    ],
})
export class WebBackgroundImageSettingsComponent {
    public readonly allowImageEffects = input<boolean>(true);
    public readonly allowScrollEffect = input<boolean>(true);
    public readonly background = input<BackgroundAtDevice | null>(null);
    public readonly disabled = input<boolean>(false);

    public readonly backgroundChange = output<BackgroundAtDevice>();
    public readonly showEditImageModalEvent = output<void>();
    public readonly unsplashClickRequested = output<void>();

    private readonly imgTest = viewChild<ElementRef<HTMLImageElement>>('imgTest');

    private readonly userFacade = inject(UserFacade);
    private readonly notifyFacade = inject(NotifyFacade);
    private readonly translate = inject(TranslateService);
    private readonly themeState = inject(ThemeStateService);
    private readonly modalService = inject(BsModalService);

    public readonly backgroundRepeatOptions = backgroundRepeatOptions;
    public readonly backgroundPositionOptions = backgroundPositionOptions;
    public readonly backgroundSizeOptions = backgroundSizeOptions;
    public readonly repeatOptions = ERepeatOptions;
    public readonly scrollEffectType = EScrollEffect;
    public readonly sizeOptions = ESizeOptions;
    public readonly previewType = PreviewType;
    public readonly directoryItemCategory = DirectoryItemCategory;
    public readonly fmSourceType = FmSourceType;
    public readonly filterOverlayTypes: { type: EFilerOverlay }[] = [
        { type: EFilerOverlay.B_W },
        { type: EFilerOverlay.BLURRY },
        { type: EFilerOverlay.SEPIA },
        { type: EFilerOverlay.BLOOM },
        { type: EFilerOverlay.DARKEN },
        { type: EFilerOverlay.OPACITY },
        { type: EFilerOverlay.RADIANCE },
        { type: EFilerOverlay.TINT },
    ];
    public readonly sliderOptions: Options = {
        minLimit: 0,
        floor: 0,
        ceil: 1,
        step: 0.05,
    };

    private readonly limitImageSize: number = 5242880;
    private readonly maxImageDimensionPx: number = 8192;

    /**
     * The parent's value itself, never a copy: a copy re-read only on a new image kept showing the last
     * tab — the breakpoints share one image object — and the next edit wrote it into this one.
     */
    private readonly value = computed<BackgroundAtDevice>(() => {
        const background = this.background() ?? backgroundAtDevice(null, 'desktop');

        return {
            ...background,
            size: background.size || DEFAULT_LAYOUT.size,
            position: background.position || DEFAULT_LAYOUT.position,
            repeat: background.repeat || DEFAULT_LAYOUT.repeat,
            scrollEffect: this.allowScrollEffect()
                ? background.scrollEffect
                : { ...background.scrollEffect, isEnabled: false },
        };
    });

    public readonly image = computed(() => (this.background()?.image ?? null) as SelectedFileInterface | null);

    /** Reset by a new image or by disabling; in between, the switch opens it for a first pick. */
    public readonly showSettings = linkedSignal({
        source: () => ({ image: this.image(), isDisabled: this.disabled() }),
        computation: ({ image, isDisabled }) => !isDisabled && Boolean(image),
    });

    public readonly size = computed(() => this.value().size);
    public readonly position = computed(() => this.value().position);
    public readonly repeat = computed(() => this.value().repeat);
    public readonly overlay = computed(() => this.value().overlay);
    public readonly filter = computed(() => this.value().filter);
    public readonly scrollEffect = computed(() => this.value().scrollEffect);

    public readonly themeColors = this.themeState.selectedColors;

    private readonly userId = toSignal(this.userFacade.userId$);

    /** The editor and the filters only work on images the current user uploaded. */
    public readonly isImageOwner = computed(() => {
        const url = this.image()?.url;

        return Boolean(url) && this.userId() === Number(url.split('/')[1].substring(32));
    });

    public readonly isParallaxActive = computed(
        () => this.size() === this.sizeOptions.auto && this.allowScrollEffect()
    );

    public readonly imageSrc = computed(() => environment.fileManagerCdnUrl + this.image()?.url);

    /** `"<left> <top>"` → the focus point marker's offsets. */
    public readonly focusPointPosition = computed(() => {
        const [left, top] = String(this.position() ?? '').split(' ');

        return { left, top };
    });

    public toggleShowSettings(value: boolean): void {
        this.showSettings.set(value);

        if (!value) {
            this.removeImage();
        }
    }

    public setOverlayOpacity({ value }: ChangeContext): void {
        this.patch({ overlay: { ...this.overlay(), opacity: value } });
    }

    public setOverlayColor({ value }: ColorPickerEventPayload): void {
        if (value === this.overlay().color) return;
        this.patch({ overlay: { ...this.overlay(), color: value } });
    }

    public toggleOverlay(isEnabled: boolean): void {
        this.patch({ overlay: { ...this.overlay(), isEnabled } });
    }

    public setFilter(value: EFilerOverlay): void {
        this.patch({ filter: { ...this.filter(), value } });
    }

    public toggleFilter(isEnabled: boolean): void {
        this.patch({ filter: { ...this.filter(), isEnabled } });
    }

    public setScrollEffect(value: EScrollEffect): void {
        this.patch({ scrollEffect: { ...this.scrollEffect(), value } });
    }

    public toggleScrollEffect(isEnabled: boolean): void {
        this.patch({ scrollEffect: { ...this.scrollEffect(), isEnabled } });
    }

    public async setImage(file: SelectedFileInterface): Promise<void> {
        if (await this.isAcceptedImage(file)) {
            this.patch({ image: file as BackgroundFile });
        }
    }

    public async replaceImage({ file }: ReplacedFile): Promise<void> {
        if (await this.isAcceptedImage(file)) {
            this.patch({ image: file as BackgroundFile });
        }
    }

    public removeImage(): void {
        this.patch({ image: null, ...DEFAULT_LAYOUT });
    }

    public setRepeat(event: Event): void {
        const repeat = (event.target as HTMLSelectElement).value;

        this.patch(repeat === ERepeatOptions.repeat ? { ...DEFAULT_LAYOUT, repeat } : { repeat });
    }

    public setSize(event: Event): void {
        const size = (event.target as HTMLSelectElement).value;

        this.patch({
            size,
            ...(size !== ESizeOptions.auto ? { scrollEffect: { ...this.scrollEffect(), isEnabled: false } } : {}),
        });
    }

    public setPosition(event: Event): void {
        this.patch({ position: (event.target as HTMLSelectElement).value });
    }

    public setFocusPoint(event: MouseEvent): void {
        const rect = this.imgTest().nativeElement.getBoundingClientRect();
        const left = `${(((event.clientX - rect.x - 10) * 100) / rect.width).toFixed(2)}%`;
        const top = `${(((event.clientY - rect.y - 10) * 100) / rect.height).toFixed(2)}%`;

        this.patch({ position: `${left} ${top}` });
    }

    private async isAcceptedImage(file: SelectedFileInterface): Promise<boolean> {
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);

        if (extendedFile.size > this.limitImageSize) {
            this.notifyFacade.notifyAction(this.translate.instant('background_size_alert'), 'error', '', {
                timeOut: 5000,
            });
            return false;
        }

        if (
            extendedFile.imageHeight >= this.maxImageDimensionPx ||
            extendedFile.imageWidth >= this.maxImageDimensionPx
        ) {
            this.modalService.show(ImageSizeLimitModalComponent, { initialState: { imageName: extendedFile.name } });
            return false;
        }

        return true;
    }

    private patch(patch: Partial<BackgroundAtDevice>): void {
        this.backgroundChange.emit({ ...this.value(), ...patch });
    }
}
